import { ALL_PRODUCT_IDS } from '../subscriptions/catalog';
import type { ProductId } from '../subscriptions/types';
import { loadNativeWebViewInsets, parseNativeAppOS } from './nativeWebViewInsets';
import { isNativeWebView } from './nativeAuthBridge';

export const NATIVE_APP_RESUMED_MESSAGE_TYPE = 'NATIVE_APP_RESUMED';
export const EXTERNAL_PURCHASE_DISCLOSURE_EVENT = 'smsp:external-purchase-disclosure';

const PENDING_SESSION_ID_KEY = 'pendingCheckoutSessionId';
const PENDING_PRODUCT_ID_KEY = 'pendingCheckoutProductId';
const PENDING_ASSIGNMENT_ID_KEY = 'pendingCheckoutAssignmentId';
const PENDING_STARTED_AT_KEY = 'pendingCheckoutStartedAt';

export type PendingCheckoutContext = {
  sessionId: string;
  productId: ProductId;
  assignmentId?: number;
  startedAt: number;
};

type NativeAppResumedListener = () => void;

const resumedListeners = new Set<NativeAppResumedListener>();
let disclosureResolver: ((approved: boolean) => void) | null = null;
let messageListenerRegistered = false;

export function isIosNativeApp(): boolean {
  if (!isNativeWebView()) {
    return false;
  }
  const appOS = loadNativeWebViewInsets().appOS ?? readLocalAppOS();
  return appOS === 'ios';
}

function readLocalAppOS() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    return parseNativeAppOS(window.localStorage.getItem('appOS'));
  } catch {
    return undefined;
  }
}

function parseProductId(value: string | null): ProductId | null {
  if (!value || !ALL_PRODUCT_IDS.includes(value as ProductId)) {
    return null;
  }
  return value as ProductId;
}

export function savePendingCheckout(context: {
  sessionId: string;
  productId: ProductId;
  assignmentId?: number;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PENDING_SESSION_ID_KEY, context.sessionId);
    window.localStorage.setItem(PENDING_PRODUCT_ID_KEY, context.productId);
    if (context.assignmentId !== undefined) {
      window.localStorage.setItem(PENDING_ASSIGNMENT_ID_KEY, String(context.assignmentId));
    } else {
      window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    }
    window.localStorage.setItem(PENDING_STARTED_AT_KEY, String(Date.now()));
  } catch {
    // ignore storage failures
  }
}

export function readPendingCheckout(): PendingCheckoutContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const sessionId = window.localStorage.getItem(PENDING_SESSION_ID_KEY);
    const productId = parseProductId(window.localStorage.getItem(PENDING_PRODUCT_ID_KEY));
    const startedAtRaw = window.localStorage.getItem(PENDING_STARTED_AT_KEY);
    if (!sessionId || !productId || !startedAtRaw) {
      return null;
    }
    const assignmentRaw = window.localStorage.getItem(PENDING_ASSIGNMENT_ID_KEY);
    const assignmentId =
      assignmentRaw && assignmentRaw !== '' ? parseInt(assignmentRaw, 10) : undefined;
    const startedAt = parseInt(startedAtRaw, 10);
    if (Number.isNaN(startedAt)) {
      return null;
    }
    return {
      sessionId,
      productId,
      ...(assignmentId !== undefined && !Number.isNaN(assignmentId) ? { assignmentId } : {}),
      startedAt,
    };
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_SESSION_ID_KEY);
    window.localStorage.removeItem(PENDING_PRODUCT_ID_KEY);
    window.localStorage.removeItem(PENDING_ASSIGNMENT_ID_KEY);
    window.localStorage.removeItem(PENDING_STARTED_AT_KEY);
  } catch {
    // ignore storage failures
  }
}

export function subscribeNativeAppResumed(listener: NativeAppResumedListener): () => void {
  resumedListeners.add(listener);
  return () => {
    resumedListeners.delete(listener);
  };
}

function notifyNativeAppResumed(): void {
  resumedListeners.forEach((listener) => listener());
}

function onWindowMessage(event: MessageEvent): void {
  const data = event.data;
  if (!data || typeof data !== 'object') {
    return;
  }
  if ((data as { type?: string }).type === NATIVE_APP_RESUMED_MESSAGE_TYPE) {
    notifyNativeAppResumed();
  }
}

export function bootstrapNativeCheckoutBridge(): void {
  if (typeof window === 'undefined' || messageListenerRegistered) {
    return;
  }
  window.addEventListener('message', onWindowMessage);
  messageListenerRegistered = true;
}

export async function startExternalCheckout(checkoutUrl: string): Promise<void> {
  if (window.flutter_inappwebview?.callHandler) {
    await window.flutter_inappwebview.callHandler('startExternalCheckout', checkoutUrl);
    return;
  }
  window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
}

export function requestExternalPurchaseDisclosure(): Promise<boolean> {
  return new Promise((resolve) => {
    disclosureResolver = resolve;
    window.dispatchEvent(new CustomEvent(EXTERNAL_PURCHASE_DISCLOSURE_EVENT));
  });
}

export function resolveExternalPurchaseDisclosure(approved: boolean): void {
  disclosureResolver?.(approved);
  disclosureResolver = null;
}
